import { ClassNames } from '@emotion/react';
import {
  createElement,
  CSSProperties,
  DOMAttributes,
  ForwardedRef,
  forwardRef,
  ReactHTML,
  ReactNode,
  ReactNodeArray,
} from 'react';

type Listeners = Omit<DOMAttributes<HTMLElement>, 'children' | 'dangerouslySetInnerHTML'>;
const listenerStartRegExp = /^on[A-Z]/;

const pseudo = ['hover', 'active', 'focus', 'before', 'after'];

export interface CSSBoxProps<T extends keyof ReactHTML = 'div'> extends CSSProperties, Listeners {
  as?: T;
  className?: string;
  cssText?: string;
  children?: ReactNodeArray | ReactNode;
  hover?: CSSProperties;
  active?: CSSProperties;
  focus?: CSSProperties;
  before?: CSSProperties;
  after?: CSSProperties;
}

const CSSBoxFn = <T extends keyof ReactHTML = 'div'>(
  props: CSSBoxProps<T>,
  ref: ForwardedRef<HTMLElement | void>,
) => {
  const { children, className, as, cssText, ...rest } = props;

  const listeners = {};
  const style = {};

  Object.entries(rest).forEach(([key, value]) => {
    if (key.match(listenerStartRegExp)) {
      listeners[key] = value;
      return;
    }

    if (pseudo.includes(key)) {
      style[`:${key}`] = value;
      return;
    }

    style[key] = value;
  });

  return createElement(ClassNames, null, ({ css, cx }) => {
    const styledClass = cx(className, css(cssText || style));
    const newProps = { className: styledClass, ref, ...listeners };
    return createElement(as || 'div', newProps, children);
  });
};

export const CSSBox = forwardRef(CSSBoxFn) as <T extends keyof ReactHTML = 'div'>(
  props: CSSBoxProps<T> & { ref?: ForwardedRef<HTMLElement | void> },
) => ReturnType<typeof CSSBoxFn>;
